import React, { useState, useEffect, useContext } from "react";
import { UserContext } from "../context/UserContext";
import axios from "axios";
import TableWrapper from "../components/TableWrapper";

export default function MatchPredictions({ match, odds, h2h, standings }) {
  const { user } = useContext(UserContext);
  const userTier = user?.subscription?.tier || "trial";
  const hasAccessToAI = userTier === "pro" || userTier === "premium";
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [algorithmDetails, setAlgorithmDetails] = useState(null);
  const [predictionsGenerated, setPredictionsGenerated] = useState(false);
  const [showAllPredictions, setShowAllPredictions] = useState(true);
  const [hasValueBets, setHasValueBets] = useState(false);
  const [filterType, setFilterType] = useState("all");
  const API_URLS = [process.env.REACT_APP_BACKEND_URL];

  const isMatchPassed = () => {
    if (!match || !match.date) return false;
    const matchDate = new Date(match.date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    matchDate.setHours(0, 0, 0, 0);
    return matchDate < today;
  };

  const categories = {
    "Match Winner": [
      { key: "home", label: "Home" },
      { key: "draw", label: "Draw" },
      { key: "away", label: "Away" },
    ],
    "Double Chance": [
      { key: "homeDraw", label: "Home/Draw" },
      { key: "homeAway", label: "Home/Away" },
      { key: "drawAway", label: "Draw/Away" },
    ],
    "Goals Over/Under": [
      { key: "over1_5", label: "Over 1.5" },
      { key: "over2_5", label: "Over 2.5" },
      { key: "over3_5", label: "Over 3.5" },
      { key: "under2_5", label: "Under 2.5" },
      { key: "under3_5", label: "Under 3.5" },
      { key: "under4_5", label: "Under 4.5" },
    ],
    "Both Teams To Score": [
      { key: "bts_yes", label: "Yes" },
      { key: "bts_no", label: "No" },
    ],
    "Total - Home": [
      { key: "total_home_over0_5", label: "Over 0.5" },
      { key: "total_home_over1_5", label: "Over 1.5" },
      { key: "total_home_over2_5", label: "Over 2.5" },
      { key: "total_home_under0_5", label: "Under 0.5" },
      { key: "total_home_under1_5", label: "Under 1.5" },
      { key: "total_home_under2_5", label: "Under 2.5" },
    ],
    "Total - Away": [
      { key: "total_away_over0_5", label: "Over 0.5" },
      { key: "total_away_over1_5", label: "Over 1.5" },
      { key: "total_away_over2_5", label: "Over 2.5" },
      { key: "total_away_under0_5", label: "Under 0.5" },
      { key: "total_away_under1_5", label: "Under 1.5" },
      { key: "total_away_under2_5", label: "Under 2.5" },
    ],
  };

  const calculateBreakEvenProbability = (odd) => {
    if (!odd || odd < 1.0) return 0;
    return (1 / odd) * 100;
  };

  const getValueBetAnalysis = (winProbability, breakEvenProbability, odd) => {
    if (!odd || odd === "-")
      return {
        category: "no_odd",
        margin: 0,
        isPositive: false,
        isValueBet: false,
      };

    const oddValue = parseFloat(odd);
    const margin = winProbability - breakEvenProbability;
    const isPositive = margin > 0;

    if (isPositive) {
      if (oddValue < 1.4) {
        if (margin >= 5) {
          return {
            category: "low_odd_positive",
            margin: margin,
            isPositive: true,
            isValueBet: false,
          };
        } else {
          return {
            category: "positive_low_odds",
            margin: margin,
            isPositive: true,
            isValueBet: false,
          };
        }
      } else if (oddValue > 2.0) {
        if (margin >= 5) {
          return {
            category: "risky_odd_positive",
            margin: margin,
            isPositive: true,
            isValueBet: false,
          };
        } else {
          return {
            category: "positive_high_risk",
            margin: margin,
            isPositive: true,
            isValueBet: false,
          };
        }
      } else {
        if (margin > 20) {
          return {
            category: "high_value",
            margin: margin,
            isPositive: true,
            isValueBet: true,
          };
        } else if (margin > 10) {
          return {
            category: "value_bet",
            margin: margin,
            isPositive: true,
            isValueBet: true,
          };
        } else if (margin > 5) {
          return {
            category: "low_value",
            margin: margin,
            isPositive: true,
            isValueBet: true,
          };
        } else {
          return {
            category: "positive_other",
            margin: margin,
            isPositive: true,
            isValueBet: false,
          };
        }
      }
    } else {
      return {
        category: "no_value",
        margin: margin,
        isPositive: false,
        isValueBet: false,
      };
    }
  };

  const getRecommendation = (winProbability, breakEvenProbability, odd) => {
    if (!odd || odd === "-") {
      return {
        text: "No odd",
        color: "gray",
        isNegative: false,
      };
    }

    const margin = winProbability - breakEvenProbability;
    const marginFormatted = Math.abs(margin).toFixed(1);

    if (margin > 0) {
      return {
        text: `+${marginFormatted}%`,
        color: "green",
        isNegative: false,
      };
    } else if (margin < 0) {
      return {
        text: `-${marginFormatted}%`,
        color: "red-light",
        isNegative: true,
      };
    } else {
      return {
        text: `0.0%`,
        color: "gray",
        isNegative: false,
      };
    }
  };

  const countValueBets = () => {
    if (!results) return 0;
    let count = 0;
    Object.keys(categories).forEach((cat) => {
      categories[cat].forEach((opt) => {
        const odd = getOddFromOddsData(opt.label, cat);
        if (odd !== "-") {
          const breakEvenProb = calculateBreakEvenProbability(parseFloat(odd));
          const winProbability = results[opt.key];
          const analysis = getValueBetAnalysis(
            winProbability,
            breakEvenProb,
            odd,
          );
          if (analysis.isValueBet) {
            count++;
          }
        }
      });
    });
    return count;
  };

  const testConnection = async (url) => {
    try {
      const response = await fetch(`${url}/predictions/health`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          key: "gicu123",
        },
      });
      return response.ok;
    } catch (err) {
      return false;
    }
  };

  const fetchPredictions = async () => {
    if (!match?.id) {
      setError("No match data available");
      return;
    }

    setLoading(true);
    setError(null);
    setAlgorithmDetails(null);
    setPredictionsGenerated(false);

    try {
      let workingUrl = null;
      for (const url of API_URLS) {
        const isWorking = await testConnection(url);
        if (isWorking) {
          workingUrl = url;
          break;
        }
      }

      if (!workingUrl) {
        throw new Error("Cannot connect to prediction server.");
      }

      const url = `${workingUrl}/predictions/calculate/${match.id}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          key: "gicu123",
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error("Prediction service not available");
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        setResults(data.predictions);
        setAlgorithmDetails(data.algorithmDetails || { method: "advanced" });
        setPredictionsGenerated(true);

        try {
          await axios.post(
            `${process.env.REACT_APP_BACKEND_URL}/predictions/save-analysis`,
            {
              matchId: match?.id,
              userId: user?.id || null,
            },
            {
              headers: {
                "Content-Type": "application/json",
                key: process.env.REACT_APP_ACCESKEY || "test-key",
              },
            },
          );
          console.log("✅ AI Analysis saved to database");
        } catch (saveError) {
          console.error("Error saving AI analysis to database:", saveError);
        }

        let foundValueBets = false;
        Object.keys(categories).forEach((cat) => {
          categories[cat].forEach((opt) => {
            const currentOdd = getOddFromOddsData(opt.label, cat);
            if (currentOdd !== "-") {
              const breakEvenProb = calculateBreakEvenProbability(
                parseFloat(currentOdd),
              );
              const winProbability = data.predictions[opt.key];
              const analysis = getValueBetAnalysis(
                winProbability,
                breakEvenProb,
                currentOdd,
              );
              if (analysis.isValueBet) {
                foundValueBets = true;
              }
            }
          });
        });

        setHasValueBets(foundValueBets);
        setShowAllPredictions(true);
      } else {
        if (data.fallback) {
          setResults(data.fallback);
          setAlgorithmDetails({ method: "fallback", reason: data.error });
          setPredictionsGenerated(true);
          setHasValueBets(false);
          setShowAllPredictions(true);
        } else {
          throw new Error(data.error || "Failed to calculate analysis");
        }
      }
    } catch (err) {
      console.error("Error:", err);
      setError(err.message);
      const fallbackProbs = calculateFallbackProbabilities();
      setResults(fallbackProbs);
      setAlgorithmDetails({ method: "calculated", reason: err.message });
      setPredictionsGenerated(true);
      setHasValueBets(false);
      setShowAllPredictions(true);
    } finally {
      setLoading(false);
    }
  };

  const countPositiveEdges = () => {
    if (!results) return 0;
    let count = 0;
    Object.keys(categories).forEach((cat) => {
      categories[cat].forEach((opt) => {
        const odd = getOddFromOddsData(opt.label, cat);
        if (odd !== "-") {
          const breakEvenProb = calculateBreakEvenProbability(parseFloat(odd));
          const winProbability = results[opt.key];
          const margin = winProbability - breakEvenProb;
          if (margin > 0) {
            count++;
          }
        }
      });
    });
    return count;
  };

  const calculateFallbackProbabilities = () => {
    if (standings && Array.isArray(standings) && standings.length > 0) {
      try {
        const homeTeam = match?.homeTeam;
        const awayTeam = match?.awayTeam;

        if (!homeTeam || !awayTeam) {
          return getGenericProbabilities();
        }

        const homeStats = standings.find(
          (team) =>
            team.team_name?.toLowerCase().includes(homeTeam.toLowerCase()) ||
            team.name?.toLowerCase().includes(homeTeam.toLowerCase()),
        );

        const awayStats = standings.find(
          (team) =>
            team.team_name?.toLowerCase().includes(awayTeam.toLowerCase()) ||
            team.name?.toLowerCase().includes(awayTeam.toLowerCase()),
        );

        if (homeStats && awayStats) {
          return calculateFromStandings(homeStats, awayStats);
        }
      } catch (error) {
        console.error("Error in standings calculation:", error);
      }
    }

    if (h2h && (h2h.home?.lastMatches || h2h.away?.lastMatches || h2h.h2h)) {
      return calculateFromH2H(h2h);
    }

    return getGenericProbabilities();
  };

  const calculateFromStandings = (homeStats, awayStats) => {
    const homePlayed = Math.max(homeStats.played || 1, 1);
    const awayPlayed = Math.max(awayStats.played || 1, 1);
    const homeAvgGoals = (homeStats.goals_for || 0) / homePlayed;
    const awayAvgGoals = (awayStats.goals_for || 0) / awayPlayed;
    const expectedGoals = homeAvgGoals * 1.15 + awayAvgGoals * 0.85;
    const over2_5 = Math.min(80, Math.max(20, (expectedGoals / 3) * 100));
    const over1_5 = Math.min(95, over2_5 + 25);
    const over3_5 = Math.min(60, Math.max(10, (expectedGoals / 4) * 100));

    const homeWinProb = Math.min(
      80,
      Math.max(20, (homeAvgGoals / (homeAvgGoals + awayAvgGoals)) * 100 * 1.1),
    );
    const awayWinProb = Math.min(
      80,
      Math.max(20, (awayAvgGoals / (homeAvgGoals + awayAvgGoals)) * 100 * 0.9),
    );
    const drawProb = Math.max(1, 100 - homeWinProb - awayWinProb);

    return {
      over1_5: Math.round(over1_5),
      over2_5: Math.round(over2_5),
      over3_5: Math.round(over3_5),
      under2_5: Math.round(100 - over2_5),
      under3_5: Math.round(Math.min(95, over2_5 + 15)),
      under4_5: Math.round(Math.min(98, over2_5 + 35)),
      bts_yes: 50,
      bts_no: 50,
      home: Math.round(homeWinProb),
      draw: Math.round(drawProb),
      away: Math.round(awayWinProb),
      homeDraw: Math.round(Math.min(99, homeWinProb + drawProb)),
      homeAway: Math.round(Math.min(99, homeWinProb + awayWinProb)),
      drawAway: Math.round(Math.min(99, drawProb + awayWinProb)),
      total_home_over0_5: 85,
      total_home_over1_5: 60,
      total_home_over2_5: 30,
      total_home_under0_5: 15,
      total_home_under1_5: 40,
      total_home_under2_5: 70,
      total_away_over0_5: 80,
      total_away_over1_5: 55,
      total_away_over2_5: 25,
      total_away_under0_5: 20,
      total_away_under1_5: 45,
      total_away_under2_5: 75,
    };
  };

  const calculateFromH2H = (h2hData) => {
    let totalGoals = 0;
    let matchCount = 0;

    if (h2hData.home?.lastMatches) {
      h2hData.home.lastMatches.forEach((match) => {
        if (match.goals_for !== null && match.goals_against !== null) {
          totalGoals += match.goals_for + match.goals_against;
          matchCount++;
        }
      });
    }

    if (h2hData.away?.lastMatches) {
      h2hData.away.lastMatches.forEach((match) => {
        if (match.goals_for !== null && match.goals_against !== null) {
          totalGoals += match.goals_for + match.goals_against;
          matchCount++;
        }
      });
    }

    if (h2hData.h2h) {
      h2hData.h2h.forEach((match) => {
        if (match.home_goals !== null && match.away_goals !== null) {
          totalGoals += match.home_goals + match.away_goals;
          matchCount++;
        }
      });
    }

    if (matchCount === 0) {
      return getGenericProbabilities();
    }

    const avgGoals = totalGoals / matchCount;
    const over2_5 = Math.min(80, Math.max(20, (avgGoals / 3.5) * 100));
    const over1_5 = Math.min(95, over2_5 + 25);
    const over3_5 = Math.min(60, Math.max(10, (avgGoals / 4.5) * 100));

    let homeWins = 0;
    let draws = 0;
    let awayWins = 0;
    let totalMatches = 0;

    if (h2hData.h2h) {
      h2hData.h2h.forEach((match) => {
        if (match.home_goals !== null && match.away_goals !== null) {
          totalMatches++;
          if (match.home_goals > match.away_goals) homeWins++;
          else if (match.home_goals === match.away_goals) draws++;
          else awayWins++;
        }
      });
    }

    const homeWinProb = totalMatches > 0 ? (homeWins / totalMatches) * 100 : 40;
    const drawProb = totalMatches > 0 ? (draws / totalMatches) * 100 : 30;
    const awayWinProb = totalMatches > 0 ? (awayWins / totalMatches) * 100 : 30;

    return {
      over1_5: Math.round(over1_5),
      over2_5: Math.round(over2_5),
      over3_5: Math.round(over3_5),
      under2_5: Math.round(100 - over2_5),
      under3_5: Math.round(Math.min(95, over2_5 + 20)),
      under4_5: Math.round(Math.min(98, over2_5 + 40)),
      bts_yes: 50,
      bts_no: 50,
      home: Math.round(homeWinProb),
      draw: Math.round(drawProb),
      away: Math.round(awayWinProb),
      homeDraw: Math.round(Math.min(99, homeWinProb + drawProb)),
      homeAway: Math.round(Math.min(99, homeWinProb + awayWinProb)),
      drawAway: Math.round(Math.min(99, drawProb + awayWinProb)),
      total_home_over0_5: 85,
      total_home_over1_5: 60,
      total_home_over2_5: 30,
      total_home_under0_5: 15,
      total_home_under1_5: 40,
      total_home_under2_5: 70,
      total_away_over0_5: 80,
      total_away_over1_5: 55,
      total_away_over2_5: 25,
      total_away_under0_5: 20,
      total_away_under1_5: 45,
      total_away_under2_5: 75,
    };
  };

  const getGenericProbabilities = () => {
    const homeTeam = match?.homeTeam?.toLowerCase() || "";
    const awayTeam = match?.awayTeam?.toLowerCase() || "";

    const bigTeams = [
      "liverpool",
      "manchester",
      "barcelona",
      "real madrid",
      "bayern",
      "psg",
      "tottenham",
    ];
    const isBigMatch =
      bigTeams.some((team) => homeTeam.includes(team)) &&
      bigTeams.some((team) => awayTeam.includes(team));

    const hasBigTeam = bigTeams.some(
      (team) => homeTeam.includes(team) || awayTeam.includes(team),
    );

    if (isBigMatch) {
      return {
        over1_5: 85,
        over2_5: 65,
        over3_5: 40,
        under2_5: 35,
        under3_5: 70,
        under4_5: 85,
        bts_yes: 60,
        bts_no: 40,
        home: 45,
        draw: 30,
        away: 25,
        homeDraw: 75,
        homeAway: 70,
        drawAway: 55,
        total_home_over0_5: 90,
        total_home_over1_5: 70,
        total_home_over2_5: 40,
        total_home_under0_5: 10,
        total_home_under1_5: 30,
        total_home_under2_5: 60,
        total_away_over0_5: 85,
        total_away_over1_5: 65,
        total_away_over2_5: 35,
        total_away_under0_5: 15,
        total_away_under1_5: 35,
        total_away_under2_5: 65,
      };
    } else if (hasBigTeam) {
      return {
        over1_5: 80,
        over2_5: 55,
        over3_5: 30,
        under2_5: 45,
        under3_5: 75,
        under4_5: 90,
        bts_yes: 55,
        bts_no: 45,
        home: 50,
        draw: 30,
        away: 20,
        homeDraw: 80,
        homeAway: 70,
        drawAway: 50,
        total_home_over0_5: 85,
        total_home_over1_5: 60,
        total_home_over2_5: 30,
        total_home_under0_5: 15,
        total_home_under1_5: 40,
        total_home_under2_5: 70,
        total_away_over0_5: 80,
        total_away_over1_5: 55,
        total_away_over2_5: 25,
        total_away_under0_5: 20,
        total_away_under1_5: 45,
        total_away_under2_5: 75,
      };
    } else {
      return {
        over1_5: 75,
        over2_5: 50,
        over3_5: 25,
        under2_5: 50,
        under3_5: 80,
        under4_5: 95,
        bts_yes: 50,
        bts_no: 50,
        home: 45,
        draw: 30,
        away: 25,
        homeDraw: 75,
        homeAway: 70,
        drawAway: 55,
        total_home_over0_5: 80,
        total_home_over1_5: 55,
        total_home_over2_5: 25,
        total_home_under0_5: 20,
        total_home_under1_5: 45,
        total_home_under2_5: 75,
        total_away_over0_5: 75,
        total_away_over1_5: 50,
        total_away_over2_5: 20,
        total_away_under0_5: 25,
        total_away_under1_5: 50,
        total_away_under2_5: 80,
      };
    }
  };

  const getOddFromOddsData = (optionLabel, category) => {
    if (!odds?.response?.[0]?.bookmakers?.[0]?.bets) return "-";

    const allMarkets = odds.response[0].bookmakers[0].bets;

    if (category === "Total - Home") {
      const market = allMarkets.find(
        (bet) => bet.name?.toLowerCase() === "total - home",
      );

      if (!market) {
        const marketNames = [
          "total - home",
          "home team total goals",
          "home team goals over/under",
          "home over/under",
          "home goals",
          "home team total",
          "total goals home team",
          "home total goals",
        ];

        for (const marketName of marketNames) {
          const foundMarket = allMarkets.find((bet) =>
            bet.name?.toLowerCase().includes(marketName),
          );
          if (foundMarket) {
            return findOddInMarket(foundMarket, optionLabel, "home");
          }
        }
      } else {
        return findOddInMarket(market, optionLabel, "home");
      }

      return "-";
    }

    if (category === "Total - Away") {
      const market = allMarkets.find(
        (bet) => bet.name?.toLowerCase() === "total - away",
      );

      if (!market) {
        const marketNames = [
          "total - away",
          "away team total goals",
          "away team goals over/under",
          "away over/under",
          "away goals",
          "away team total",
          "total goals away team",
          "away total goals",
        ];

        for (const marketName of marketNames) {
          const foundMarket = allMarkets.find((bet) =>
            bet.name?.toLowerCase().includes(marketName),
          );
          if (foundMarket) {
            return findOddInMarket(foundMarket, optionLabel, "away");
          }
        }
      } else {
        return findOddInMarket(market, optionLabel, "away");
      }

      return "-";
    }

    function findOddInMarket(market, optionLabel, teamType = "home") {
      if (!market?.values) return "-";

      const optionMapping = {
        "Over 0.5": ["over 0.5", "over0.5", "o 0.5", "over 0.5 goals"],
        "Over 1.5": ["over 1.5", "over1.5", "o 1.5", "over 1.5 goals"],
        "Over 2.5": ["over 2.5", "over2.5", "o 2.5", "over 2.5 goals"],
        "Under 0.5": ["under 0.5", "under0.5", "u 0.5", "under 0.5 goals"],
        "Under 1.5": ["under 1.5", "under1.5", "u 1.5", "under 1.5 goals"],
        "Under 2.5": ["under 2.5", "under2.5", "u 2.5", "under 2.5 goals"],
      };

      const searchTerms = optionMapping[optionLabel] || [
        optionLabel.toLowerCase(),
      ];

      for (const value of market.values) {
        const valueName = value.value?.toLowerCase();

        for (const term of searchTerms) {
          if (valueName && valueName.includes(term)) {
            return value.odd || "-";
          }
        }
      }

      for (const value of market.values) {
        const valueName = value.value?.toLowerCase();
        const labelLower = optionLabel.toLowerCase();

        if (valueName) {
          const hasOver =
            labelLower.includes("over") && valueName.includes("over");
          const hasUnder =
            labelLower.includes("under") && valueName.includes("under");
          const hasNumber = labelLower.match(/\d\.\d/);

          if (hasNumber && (hasOver || hasUnder)) {
            const number = labelLower.match(/\d\.\d/)[0];
            if (valueName.includes(number)) {
              return value.odd || "-";
            }
          }
        }
      }

      return "-";
    }

    if (category === "Match Winner") {
      const matchWinnerMarket = allMarkets.find(
        (bet) =>
          bet.name?.toLowerCase().includes("match winner") ||
          bet.name?.toLowerCase().includes("1x2") ||
          bet.name?.toLowerCase().includes("full time result"),
      );

      if (!matchWinnerMarket?.values) return "-";

      const optionMapping = {
        Home: ["home", "1"],
        Draw: ["draw", "x"],
        Away: ["away", "2"],
      };

      const optionNames = optionMapping[optionLabel] || [
        optionLabel.toLowerCase(),
      ];
      const value = matchWinnerMarket.values.find((v) =>
        optionNames.some((name) =>
          v.value?.toLowerCase().includes(name.toLowerCase()),
        ),
      );

      return value?.odd || "-";
    }

    if (category === "Double Chance") {
      const doubleChanceMarket = allMarkets.find(
        (bet) =>
          bet.name?.toLowerCase().includes("double chance") ||
          bet.name?.toLowerCase().includes("1x"),
      );

      if (!doubleChanceMarket?.values) return "-";

      const optionMapping = {
        "Home/Draw": ["1x", "home/draw"],
        "Home/Away": ["12", "home/away"],
        "Draw/Away": ["x2", "draw/away"],
      };

      const optionNames = optionMapping[optionLabel] || [
        optionLabel.toLowerCase(),
      ];
      const value = doubleChanceMarket.values.find((v) =>
        optionNames.some((name) =>
          v.value?.toLowerCase().includes(name.toLowerCase()),
        ),
      );

      return value?.odd || "-";
    }

    if (category === "Goals Over/Under") {
      const overUnderMarket = allMarkets.find(
        (bet) =>
          bet.name?.toLowerCase().includes("over/under") ||
          bet.name?.toLowerCase().includes("goals over/under"),
      );

      if (!overUnderMarket?.values) return "-";

      const optionMapping = {
        "Over 1.5": ["over 1.5", "over 1.5 goals"],
        "Over 2.5": ["over 2.5", "over 2.5 goals"],
        "Over 3.5": ["over 3.5", "over 3.5 goals"],
        "Under 2.5": ["under 2.5", "under 2.5 goals"],
        "Under 3.5": ["under 3.5", "under 3.5 goals"],
        "Under 4.5": ["under 4.5", "under 4.5 goals"],
      };

      const optionNames = optionMapping[optionLabel] || [
        optionLabel.toLowerCase(),
      ];
      const value = overUnderMarket.values.find((v) =>
        optionNames.some((name) =>
          v.value?.toLowerCase().includes(name.toLowerCase()),
        ),
      );

      return value?.odd || "-";
    }

    if (category === "Both Teams To Score") {
      const btsMarket = allMarkets.find(
        (bet) =>
          bet.name?.toLowerCase().includes("both teams to score") ||
          bet.name?.toLowerCase().includes("both teams score"),
      );

      if (!btsMarket?.values) return "-";

      const optionMapping = {
        Yes: ["yes", "both teams to score - yes"],
        No: ["no", "both teams to score - no"],
      };

      const optionNames = optionMapping[optionLabel] || [
        optionLabel.toLowerCase(),
      ];
      const value = btsMarket.values.find((v) =>
        optionNames.some((name) =>
          v.value?.toLowerCase().includes(name.toLowerCase()),
        ),
      );

      return value?.odd || "-";
    }

    return "-";
  };

  const getPercentageColor = (percentage) => {
    const green = [34, 197, 94];
    const yellow = [255, 193, 7];

    let r, g, b;

    if (percentage < 50) {
      const factor = percentage / 50;
      r = Math.round(yellow[0] - (yellow[0] - 255) * factor);
      g = Math.round(yellow[1] - (yellow[1] - 240) * factor);
      b = Math.round(yellow[2] - (yellow[2] - 220) * factor);
    } else {
      const factor = (percentage - 50) / 50;
      r = Math.round(yellow[0] + (green[0] - yellow[0]) * factor);
      g = Math.round(yellow[1] + (green[1] - yellow[1]) * factor);
      b = Math.round(yellow[2] + (green[2] - yellow[2]) * factor);
    }

    return `rgb(${r}, ${g}, ${b})`;
  };

  const ProgressBar = ({ percentage }) => {
    const backgroundColor = getPercentageColor(percentage);

    return (
      <div className="flex items-center gap-2 w-full">
        <div className="flex-1 bg-gray-700 rounded-full h-2 overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-500 ease-out"
            style={{
              width: `${percentage}%`,
              backgroundColor: backgroundColor,
            }}
          />
        </div>
        <span
          className="text-xs font-semibold px-2 py-1 rounded min-w-[45px] text-center text-white"
          style={{
            backgroundColor: backgroundColor,
          }}
        >
          {percentage}%
        </span>
      </div>
    );
  };

  const ExpectedGoalsDisplay = () => {
    const expectedGoals =
      algorithmDetails?.calculatedExpectedGoals ||
      algorithmDetails?.expectedGoals ||
      algorithmDetails?.factors?.finalExpectedGoalsLimited ||
      algorithmDetails?.factors?.finalExpectedGoals ||
      0;

    const displayValue =
      typeof expectedGoals === "number" ? expectedGoals.toFixed(2) : "0.00";

    const getExpectedGoalsColor = () => {
      const value = parseFloat(displayValue);
      if (value < 2.0) return "text-red-400";
      if (value < 3.0) return "text-green-400";
      if (value < 4.0) return "text-green-500";
      return "text-red-400";
    };

    return (
      <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-800 rounded-lg border border-gray-700 shadow-sm">
        <span className="text-sm font-semibold text-gray-300">
          Expected Goals:
        </span>
        <span
          className={`text-base font-bold px-2 py-0.5 rounded bg-gray-900 ${getExpectedGoalsColor()}`}
        >
          {displayValue}
        </span>
      </div>
    );
  };

  const BreakEvenDisplay = ({ odd }) => {
    if (!odd || odd === "-")
      return <span className="text-gray-500 text-sm">-</span>;

    const breakEvenProb = calculateBreakEvenProbability(parseFloat(odd));

    return (
      <div className="flex flex-col items-center">
        <span className="text-sm font-semibold text-blue-400">
          {breakEvenProb.toFixed(1)}%
        </span>
      </div>
    );
  };

  const ValueBetIndicator = ({ winProbability, breakEvenProbability, odd }) => {
    if (!odd || odd === "-")
      return <span className="text-gray-500 text-sm">-</span>;

    const analysis = getValueBetAnalysis(
      winProbability,
      breakEvenProbability,
      odd,
    );

    if (analysis.category === "high_value") {
      return (
        <div className="flex items-center justify-center px-3 py-1.5 rounded-lg bg-green-900/30 border border-green-500/50">
          <span className="text-sm font-bold text-green-400">Strong</span>
        </div>
      );
    } else if (analysis.category === "value_bet") {
      return (
        <div className="flex items-center justify-center px-3 py-1.5 rounded-lg bg-green-900/30 border border-green-500/50">
          <span className="text-sm font-bold text-green-400">Moderate</span>
        </div>
      );
    } else if (analysis.category === "low_value") {
      return (
        <div className="flex items-center justify-center px-3 py-1.5 rounded-lg bg-green-900/30 border border-green-500/50">
          <span className="text-sm font-bold text-green-400">Low</span>
        </div>
      );
    } else if (analysis.category === "low_odd_positive") {
      return (
        <div className="flex items-center justify-center px-3 py-1.5 rounded-lg bg-green-900/20 border border-green-500/30">
          <span className="text-sm font-bold text-green-300">Low</span>
        </div>
      );
    } else if (analysis.category === "positive_low_odds") {
      return (
        <div className="flex items-center justify-center px-3 py-1.5 rounded-lg bg-green-900/20 border border-green-500/30">
          <span className="text-sm font-bold text-green-300">Positive</span>
        </div>
      );
    } else if (analysis.category === "risky_odd_positive") {
      return (
        <div className="flex items-center justify-center px-3 py-1.5 rounded-lg bg-green-900/20 border border-green-500/30">
          <span className="text-sm font-bold text-green-300">Risky</span>
        </div>
      );
    } else if (analysis.category === "positive_high_risk") {
      return (
        <div className="flex items-center justify-center px-3 py-1.5 rounded-lg bg-green-900/20 border border-green-500/30">
          <span className="text-sm font-bold text-green-300">Positive</span>
        </div>
      );
    } else if (analysis.category === "positive_other") {
      return (
        <div className="flex items-center justify-center px-3 py-1.5 rounded-lg bg-green-900/20 border border-green-500/30">
          <span className="text-sm font-bold text-green-300">Positive</span>
        </div>
      );
    } else if (analysis.category === "no_value") {
      return (
        <div className="flex items-center justify-center px-3 py-1.5 rounded-lg bg-red-900/30 border border-red-500/50">
          <span className="text-sm font-bold text-red-400">No Value</span>
        </div>
      );
    }

    return <span className="text-gray-500 text-sm">-</span>;
  };

  const FilterToggle = () => {
    const valueBetsCount = countValueBets();
    const positiveEdgeCount = countPositiveEdges();

    if (valueBetsCount === 0 && positiveEdgeCount === 0) {
      return (
        <div className="bg-gradient-to-r from-gray-800 to-gray-900 rounded-lg border border-gray-700 p-4 shadow-lg">
          <div className="flex items-center justify-between">
            <ExpectedGoalsDisplay />
            <div className="text-center text-sm text-gray-400">
              No value events or positive edges found
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="bg-gradient-to-r from-gray-800 to-gray-900 rounded-lg border border-gray-700 p-4 shadow-lg">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          {/* Expected Goals - left side */}
          <div className="flex-shrink-0">
            <ExpectedGoalsDisplay />
          </div>

          {/* Buttons container - center */}
          <div className="flex-1 flex flex-col sm:flex-row items-center justify-center gap-3">
            {valueBetsCount > 0 && (
              <button
                className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 flex items-center gap-2 ${
                  filterType === "valueBets"
                    ? "bg-green-600 text-white shadow-md hover:bg-green-700 hover:shadow-[0_0_15px_rgba(34,197,94,0.5)]"
                    : "bg-gray-700 text-gray-300 hover:bg-gray-600 hover:shadow-[0_0_10px_rgba(34,197,94,0.3)] hover:text-green-300 hover:border-green-500/50 border border-transparent hover:border-green-500/50"
                }`}
                onClick={() => setFilterType("valueBets")}
              >
                Show Only Value Events
                <span className="bg-white text-green-600 text-xs font-bold px-2 py-1 rounded-full">
                  {valueBetsCount}
                </span>
              </button>
            )}

            {positiveEdgeCount > 0 && (
              <button
                className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 flex items-center gap-2 ${
                  filterType === "positiveEdge"
                    ? "bg-blue-600 text-white shadow-md hover:bg-blue-700 hover:shadow-[0_0_15px_rgba(59,130,246,0.5)]"
                    : "bg-gray-700 text-gray-300 hover:bg-gray-600 hover:shadow-[0_0_10px_rgba(59,130,246,0.3)] hover:text-blue-300 hover:border-blue-500/50 border border-transparent hover:border-blue-500/50"
                }`}
                onClick={() => setFilterType("positiveEdge")}
              >
                Show Only Positive Edge
                <span className="bg-white text-blue-600 text-xs font-bold px-2 py-1 rounded-full">
                  {positiveEdgeCount}
                </span>
              </button>
            )}

            {filterType !== "all" && (
              <button
                className="px-4 py-2 rounded-lg font-medium transition-all duration-300 bg-gray-700 text-gray-300 hover:bg-gray-600 hover:shadow-[0_0_10px_rgba(156,163,175,0.3)] hover:text-white border border-transparent hover:border-gray-500"
                onClick={() => setFilterType("all")}
              >
                Show All Analysis
              </button>
            )}
          </div>

          {/* Status text - right side */}
          <div className="flex-shrink-0 text-sm text-gray-400">
            {filterType === "valueBets" &&
              `Showing ${valueBetsCount} value event${valueBetsCount === 1 ? "" : "s"}`}
            {filterType === "positiveEdge" &&
              `Showing ${positiveEdgeCount} positive edge${positiveEdgeCount === 1 ? "" : "s"}`}
            {filterType === "all" && "Showing all analysis"}
          </div>
        </div>
      </div>
    );
  };

  const PredictionCard = ({ category, options }) => {
    const filteredOptions = options.filter((opt) => {
      if (filterType === "all") return true;

      const currentOdd = getOddFromOddsData(opt.label, category);
      if (currentOdd === "-") return false;

      const breakEvenProb = calculateBreakEvenProbability(
        parseFloat(currentOdd),
      );
      const winProbability = results[opt.key];
      const margin = winProbability - breakEvenProb;

      if (filterType === "valueBets") {
        const analysis = getValueBetAnalysis(
          winProbability,
          breakEvenProb,
          currentOdd,
        );
        return analysis.isValueBet;
      }

      if (filterType === "positiveEdge") {
        return margin > 0;
      }

      return true;
    });

    if (filteredOptions.length === 0) return null;

    const tableHeaderClass =
      "text-center py-3 px-4 text-xs font-medium text-gray-400 uppercase tracking-wider border-b border-gray-700";

    return (
      <div className="bg-gray-800/50 rounded-lg border border-gray-700 shadow-sm overflow-hidden">
        <div className="bg-gray-800 px-4 py-3 border-b border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-base font-semibold text-gray-200">
                {category}
              </h3>
            </div>
          </div>
        </div>

        {/* Desktop View */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm">
            <colgroup>
              <col className="w-[15%]" />
              <col className="w-[10%]" />
              <col className="w-[10%]" />
              <col className="w-[25%]" />
              <col className="w-[15%]" />
              <col className="w-[15%]" />
            </colgroup>
            <thead>
              <tr>
                <th className={tableHeaderClass}>Market</th>
                <th className={tableHeaderClass}>Odds</th>
                <th className={tableHeaderClass}>Break-Even</th>
                <th className={tableHeaderClass}>Model Probability</th>
                <th className={tableHeaderClass}>Value Edge</th>
                <th className={tableHeaderClass}>Level</th>
              </tr>
            </thead>
            <tbody>
              {filteredOptions.map((opt, index) => {
                const currentOdd = getOddFromOddsData(opt.label, category);
                const breakEvenProb =
                  currentOdd !== "-"
                    ? calculateBreakEvenProbability(parseFloat(currentOdd))
                    : 0;
                const winProbability = results ? results[opt.key] : 0;
                const recommendation = getRecommendation(
                  winProbability,
                  breakEvenProb,
                  currentOdd,
                );
                const analysis = getValueBetAnalysis(
                  winProbability,
                  breakEvenProb,
                  currentOdd,
                );

                return (
                  <tr
                    key={opt.key}
                    className={`border-b border-gray-700 transition-colors hover:bg-gray-700/30 ${
                      index === filteredOptions.length - 1 ? "" : ""
                    }`}
                  >
                    <td className="py-3 px-4 text-center">
                      <span className="font-medium text-gray-300 text-sm">
                        {opt.label}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span
                        className={`inline-flex items-center justify-center px-2 py-1 rounded text-sm font-medium ${
                          recommendation.isNegative
                            ? "bg-red-900/30 text-red-400 border border-red-700/50"
                            : analysis.isValueBet
                              ? "bg-green-900/30 text-green-400 border border-green-700/50"
                              : analysis.category === "positive_low_odds" ||
                                  analysis.category === "positive_high_risk" ||
                                  analysis.category === "positive_other" ||
                                  analysis.category === "low_odd_positive" ||
                                  analysis.category === "risky_odd_positive"
                                ? "bg-green-900/20 text-green-300 border border-green-700/30"
                                : analysis.category === "no_value"
                                  ? "bg-gray-800 text-gray-400 border border-gray-700"
                                  : "bg-blue-900/30 text-blue-400 border border-blue-700/50"
                        } min-w-[50px]`}
                      >
                        {currentOdd}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <BreakEvenDisplay odd={currentOdd} />
                    </td>
                    <td className="py-3 px-4">
                      <ProgressBar percentage={results[opt.key]} />
                    </td>
                    <td className="py-3 px-4 text-center">
                      <div
                        className={`inline-flex items-center justify-center px-3 py-1.5 rounded text-sm font-semibold ${
                          recommendation.isNegative
                            ? "bg-red-900/30 text-red-400 border border-red-700/50"
                            : recommendation.color === "green"
                              ? "bg-green-900/30 text-green-400 border border-green-700/50"
                              : "bg-gray-800 text-gray-400 border border-gray-700"
                        }`}
                      >
                        {recommendation.text}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <ValueBetIndicator
                        winProbability={winProbability}
                        breakEvenProbability={breakEvenProb}
                        odd={currentOdd}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Mobile View */}
        <div className="md:hidden space-y-3 p-4">
          {filteredOptions.map((opt, index) => {
            const currentOdd = getOddFromOddsData(opt.label, category);
            const breakEvenProb =
              currentOdd !== "-"
                ? calculateBreakEvenProbability(parseFloat(currentOdd))
                : 0;
            const winProbability = results ? results[opt.key] : 0;
            const recommendation = getRecommendation(
              winProbability,
              breakEvenProb,
              currentOdd,
            );
            const analysis = getValueBetAnalysis(
              winProbability,
              breakEvenProb,
              currentOdd,
            );

            return (
              <div
                key={opt.key}
                className="bg-gray-800/70 rounded-lg p-3 border border-gray-700 space-y-2"
              >
                <div className="flex justify-between items-center">
                  <span className="font-medium text-gray-300 text-sm">
                    {opt.label}
                  </span>
                  <span
                    className={`px-2 py-1 rounded text-sm font-medium ${
                      recommendation.isNegative
                        ? "bg-red-900/30 text-red-400 border border-red-700/50"
                        : analysis.isValueBet
                          ? "bg-green-900/30 text-green-400 border border-green-700/50"
                          : "bg-gray-800 text-gray-400 border border-gray-700"
                    }`}
                  >
                    {currentOdd}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-gray-900/50 rounded p-2">
                    <div className="text-xs text-gray-500">Break-Even</div>
                    <BreakEvenDisplay odd={currentOdd} />
                  </div>
                  <div className="bg-gray-900/50 rounded p-2">
                    <div className="text-xs text-gray-500">Value Edge</div>
                    <div
                      className={`text-sm font-semibold ${
                        recommendation.isNegative
                          ? "text-red-400"
                          : recommendation.color === "green"
                            ? "text-green-400"
                            : "text-gray-400"
                      }`}
                    >
                      {recommendation.text}
                    </div>
                  </div>
                </div>

                <div>
                  <div className="text-xs text-gray-500 mb-1">
                    Model Probability
                  </div>
                  <ProgressBar percentage={winProbability} />
                </div>

                <div className="flex justify-center pt-1">
                  <ValueBetIndicator
                    winProbability={winProbability}
                    breakEvenProbability={breakEvenProb}
                    odd={currentOdd}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (odds && odds.response && odds.response[0]?.bookmakers?.[0]?.bets) {
      console.log(
        "📊 Available markets:",
        odds.response[0].bookmakers[0].bets.map((b) => b.name),
      );

      const allMarkets = odds.response[0].bookmakers[0].bets;

      const homeTotalMarkets = allMarkets.filter(
        (bet) =>
          bet.name?.toLowerCase().includes("home") &&
          (bet.name?.toLowerCase().includes("total") ||
            bet.name?.toLowerCase().includes("goals")),
      );

      const awayTotalMarkets = allMarkets.filter(
        (bet) =>
          bet.name?.toLowerCase().includes("away") &&
          (bet.name?.toLowerCase().includes("total") ||
            bet.name?.toLowerCase().includes("goals")),
      );

      console.log("🏠 Home Total Markets found:", homeTotalMarkets.length);
      homeTotalMarkets.forEach((market) => {
        console.log(`   Market: ${market.name}`);
        if (market.values) {
          market.values.forEach((value) => {
            console.log(`     ${value.value}: ${value.odd}`);
          });
        }
      });

      console.log("✈️ Away Total Markets found:", awayTotalMarkets.length);
      awayTotalMarkets.forEach((market) => {
        console.log(`   Market: ${market.name}`);
        if (market.values) {
          market.values.forEach((value) => {
            console.log(`     ${value.value}: ${value.odd}`);
          });
        }
      });
    }
  }, [odds]);

  if (!predictionsGenerated) {
    if (isMatchPassed()) {
      return null;
    }

    return (
      <div className="text-center py-6">
        {hasAccessToAI ? (
          <button
            className="px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg shadow-lg hover:from-green-700 hover:to-green-800 transition-all duration-200 font-semibold flex items-center gap-2 mx-auto"
            onClick={fetchPredictions}
            disabled={loading}
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Generating AI Analysis...
              </>
            ) : (
              <>
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
                Generate AI Analysis
              </>
            )}
          </button>
        ) : (
          <div className="bg-gray-800 rounded-lg p-6 shadow-lg border border-gray-700">
            <div className="flex flex-col items-center gap-4">
              <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-r from-green-600 to-green-700 rounded-full">
                <svg
                  className="w-6 h-6 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
              </div>

              <div className="text-center">
                <h3 className="text-xl font-bold text-white mb-2">
                  Upgrade to Generate AI Analysis
                </h3>
                <div className="text-sm text-gray-400">
                  Current plan:{" "}
                  <span className="font-semibold capitalize">{userTier}</span>
                </div>
                <p className="text-gray-300 mb-4 max-w-md">
                  AI-powered generate analysis is available only for Pro and
                  Premium users. Upgrade your plan to access advanced match
                  analysis and value events insights.
                </p>

                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                  <button
                    onClick={() =>
                      (window.location.href = "/subscription-plans")
                    }
                    className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-bold py-3 px-6 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg"
                  >
                    View Subscription Plans
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {error && hasAccessToAI && (
          <div className="mt-4 bg-yellow-900/30 border border-yellow-700/50 rounded-lg p-3">
            <p className="text-yellow-400 text-sm font-medium">
              Error: {error}
            </p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        {isMatchPassed() ? null : <FilterToggle />}

        <PredictionCard
          category="Match Winner"
          options={categories["Match Winner"]}
        />

        <PredictionCard
          category="Double Chance"
          options={categories["Double Chance"]}
        />

        <PredictionCard
          category="Goals Over/Under"
          options={categories["Goals Over/Under"]}
        />

        <PredictionCard
          category="Both Teams To Score"
          options={categories["Both Teams To Score"]}
        />

        <PredictionCard
          category="Total - Home"
          options={categories["Total - Home"]}
        />

        <PredictionCard
          category="Total - Away"
          options={categories["Total - Away"]}
        />

        {!isMatchPassed() && (
          <div className="bg-gray-800 rounded-lg p-3 border border-gray-700">
            <h4 className="font-semibold text-gray-300 mb-2 text-sm flex items-center gap-2">
              <span className="text-green-500">⚡</span>
              AI Prediction v2 Algorithm - Complete Analysis:
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="flex items-start gap-2">
                  <span className="text-green-500">•</span>
                  <span className="text-sm text-gray-400">
                    <strong className="text-gray-300">
                      AI Powered (Level explained)
                    </strong>
                    : Not yet implemented - coming soon.
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
